import { ProcessingColumn } from '../models/corporate-action-processing-registry'
import { ProcessingRowValues } from '../models/corporate-action-processing.model'

/**
 * Excel paste handling for the Corporate Actions Processing grid (PVAL-25952),
 * implemented lib-locally and driven by the active registry entry's `columns`
 * array.
 *
 * `PasteService.multiValueContinuousPaste` from `@helios/create-transactions-modal`
 * is deliberately NOT reused: it is not exported from that lib's public API, it
 * indexes into `columnApi.getAllDisplayedColumns()` (which here includes the
 * leading validity-status column and the trailing delete column, offsetting
 * every pasted value), and it anchors on a user-selected cell range rather than
 * appending new rows. This ticket's requirement is "split on newlines/tabs, map
 * positionally to the visible column set, append as new rows".
 *
 * Positional mapping is against `columns` — the SAME array that builds the
 * colDefs and the Copy Headers line — so a future column-set change moves header
 * order, paste order and colDefs together.
 */

/** Header line values Copy Headers produces, used to detect a pasted header row. */
function isHeaderLine(cells: string[], columns: ProcessingColumn[]): boolean {
  if (cells.length === 0) {
    return false
  }
  const headers = columns.map(column => column.headerName.toLowerCase())
  return cells.every(
    (cell, index) =>
      cell.trim().length === 0 || headers[index] === cell.trim().toLowerCase(),
  )
}

/**
 * Parse tab-delimited clipboard text into partial row values, mapped positionally
 * to `columns`.
 *
 * - splits on `\r\n`, `\r` or `\n`, then on tabs
 * - drops entirely-blank lines (including Excel's trailing newline)
 * - drops a leading line that exactly matches the Copy Headers output
 * - ignores extra columns beyond `columns.length`; missing trailing cells are
 *   simply left unset (they stay blank in the grid and fail validation normally)
 */
export function parsePastedProcessingRows(
  clipboardText: string | undefined | null,
  columns: ProcessingColumn[],
): ProcessingRowValues[] {
  if (!clipboardText || columns.length === 0) {
    return []
  }

  const lines = clipboardText
    .split(/\r\n|\r|\n/)
    .map(line => line.split('\t'))
    .filter(cells => cells.some(cell => cell.trim().length > 0))

  const dataLines =
    lines.length > 0 && isHeaderLine(lines[0], columns) ? lines.slice(1) : lines

  return dataLines.map(cells => {
    const values: ProcessingRowValues = {}
    columns.forEach((column, index) => {
      values[column.field] = (cells[index] ?? '').trim()
    })
    return values
  })
}
